import React, { useState, useRef } from "react";
import {
  Box,
  Button,
  Typography,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Alert,
  Stack,
  IconButton,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import CloseIcon from "@mui/icons-material/Close";
import { palette } from "../../themes/theme";
import * as XLSX from "xlsx";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const BatchUpload = () => {
  const [loading, setLoading] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  const [selectedFileName, setSelectedFileName] = useState("");
  const fileInputRef = useRef(null);

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setSelectedFileName(file.name);
    setLoading(true);
    setUploadResults(null);

    try {
      // Read the Excel file
      const data = await readExcelFile(file);

      // Simulate processing for 5 seconds
      setTimeout(() => {
        // For now, mark all as successful (we'll configure actual logic later)
        const results = {
          successful: data.map((item, index) => ({
            address: item.Address || `Location ${index + 1}`,
            name: item.Name || "",
            longitude: item.Longitude,
            latitude: item.Latitude,
          })),
          failed: [],
        };

        setUploadResults(results);
        setLoading(false);
      }, 5000);
    } catch (error) {
      console.error("Error reading file:", error);
      setUploadResults({
        successful: [],
        failed: [
          {
            address: "File Error",
            error: "Could not read the Excel file. Please check the format.",
          },
        ],
      });
      setLoading(false);
    }

    // Reset file input
    event.target.value = null;
  };

  const readExcelFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });

          // Get first sheet
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];

          // Convert to JSON
          const jsonData = XLSX.utils.sheet_to_json(worksheet);

          resolve(jsonData);
        } catch (error) {
          reject(error);
        }
      };

      reader.onerror = (error) => reject(error);
      reader.readAsArrayBuffer(file);
    });
  };

  const handleClearResults = () => {
    setUploadResults(null);
    setSelectedFileName("");
  };

  const handleDownloadSample = () => {
    // Download the sample Excel file
    const link = document.createElement("a");
    link.href = "/sample-locations.xlsx";
    link.download = "sample-locations.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Box sx={{ width: "100%", p: 2 }}>
      <Stack spacing={2}>
        {/* Upload Section */}
        <Box>
          <Button
            component="label"
            variant="contained"
            startIcon={<UploadFileIcon />}
            sx={{
              width: "100%",
              backgroundColor: palette.primary.main,
              color: "#fff",
              "&:hover": {
                backgroundColor: palette.primary.hover,
              },
              textTransform: "none",
              fontWeight: 600,
            }}
            disabled={loading}
          >
            {selectedFileName || "Select Excel File"}
            <VisuallyHiddenInput
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileSelect}
            />
          </Button>
        </Box>

        {/* Download Sample Link */}
        <Box sx={{ textAlign: "center" }}>
          <Button
            onClick={handleDownloadSample}
            sx={{
              textTransform: "none",
              color: palette.primary.main,
              fontSize: "12px",
              textDecoration: "underline",
              "&:hover": {
                backgroundColor: "transparent",
                textDecoration: "underline",
              },
            }}
          >
            Download Sample Excel Format
          </Button>
        </Box>

        {/* Expected Format Info */}
        <Alert severity="info" sx={{ fontSize: "12px" }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Excel file should contain:
          </Typography>
          <Typography variant="body2" component="div">
            • <strong>Address</strong>
            <br />• <strong>Name</strong>
            <br />• <strong>City</strong>
            <br />• <strong>State</strong>
            <br />• <strong>Zip</strong>
            <br />
            {/* • <strong>Longitude</strong> (optional)
            <br />• <strong>Latitude</strong> (optional) */}
          </Typography>
        </Alert>

        {/* Loading State */}
        {loading && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              py: 3,
            }}
          >
            <CircularProgress size={40} sx={{ color: palette.primary.main }} />
            <Typography
              variant="body2"
              sx={{ mt: 2, color: palette.text.secondary }}
            >
              Processing locations...
            </Typography>
          </Box>
        )}

        {/* Results Section */}
        {uploadResults && !loading && (
          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Upload Results
              </Typography>
              <IconButton size="small" onClick={handleClearResults}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Successful Uploads */}
            {uploadResults.successful.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Alert
                  severity="success"
                  icon={<CheckCircleIcon />}
                  sx={{ mb: 1 }}
                >
                  {uploadResults.successful.length} location(s) added
                  successfully
                </Alert>
                <List dense sx={{ maxHeight: "200px", overflow: "auto" }}>
                  {uploadResults.successful.map((item, index) => (
                    <ListItem
                      key={index}
                      sx={{
                        border: `1px solid ${palette.background.success}`,
                        borderRadius: "4px",
                        mb: 0.5,
                        backgroundColor: "#f1f8f4",
                      }}
                    >
                      <ListItemText
                        primary={
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {item.name || item.address}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {item.name ? item.address : ""}
                            {item.longitude && item.latitude
                              ? ` (${item.latitude.toFixed(
                                  4
                                )}, ${item.longitude.toFixed(4)})`
                              : ""}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {/* Failed Uploads */}
            {uploadResults.failed.length > 0 && (
              <Box>
                <Alert severity="error" icon={<ErrorIcon />} sx={{ mb: 1 }}>
                  {uploadResults.failed.length} location(s) failed
                </Alert>
                <List dense sx={{ maxHeight: "200px", overflow: "auto" }}>
                  {uploadResults.failed.map((item, index) => (
                    <ListItem
                      key={index}
                      sx={{
                        border: `1px solid ${palette.background.danger}`,
                        borderRadius: "4px",
                        mb: 0.5,
                        backgroundColor: "#fef1f1",
                      }}
                    >
                      <ListItemText
                        primary={
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {item.address}
                          </Typography>
                        }
                        secondary={
                          <Typography
                            variant="caption"
                            color="error"
                            sx={{ fontSize: "11px" }}
                          >
                            {item.error}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </Box>
        )}
      </Stack>
    </Box>
  );
};

export default BatchUpload;
